import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Backdrop,
  CircularProgress,
  Divider,
} from '../../../components/mui/Core/index'
import HoldAllocationNav from '../components/HoldAllocationNav/hold-allocation-nav'
import OrderInfoCard from './components/OrderInfoCard/order-info-card'
import HoldDetailsTable from './components/HoldDetailsTable/hold-details-table'
import ReleaseHoldModal from './components/ReleaseHoldModal/release-hold-modal'
import AllocationTable from './components/AllocationTable/allocation-table'
import AuditTimeline from './components/AuditTimeline/audit-timeline'
import GenericSnackbar from '../../../components/GenericSnackbar/generic-snackbar'
import PageTitle from '../../../components/PageTitle/page-title'
import SubHeader from '../../../components/SubHeader/sub-header'
import { setSnackbar } from '../../../components/GenericSnackbar/generic-snackbar-slice'
import {
  fetchOrderDetail,
  doReleaseHold,
  doReleaseAll,
  doPrefillAllocation,
  doSubmitAllocation,
  fetchAuditTimeline,
  resetDetail,
  setAllocationQty,
} from '../hold-allocation-detail-slice'

function HoldAllocationDetail() {
  const { orderId } = useParams()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const orderDetail = useSelector((s) => s.holdAllocationDetail.orderDetail)
  const holds = useSelector((s) => s.holdAllocationDetail.holds)
  const allocationLines = useSelector((s) => s.holdAllocationDetail.allocationLines)
  const auditEvents = useSelector((s) => s.holdAllocationDetail.auditEvents)
  const isFetching = useSelector((s) => s.holdAllocationDetail.isFetching)
  const isSubmitting = useSelector((s) => s.holdAllocationDetail.isSubmitting)
  const error = useSelector((s) => s.holdAllocationDetail.error)
  const submitError = useSelector((s) => s.holdAllocationDetail.submitError)

  const [mockRole, setMockRole] = useState('CUSTOMER_SERVICE')
  const [releaseModalOpen, setReleaseModalOpen] = useState(false)
  const [releaseAllModalOpen, setReleaseAllModalOpen] = useState(false)
  const [targetHold, setTargetHold] = useState(null)

  const loadDetail = useCallback(() => {
    if (orderId) {
      dispatch(fetchOrderDetail(orderId))
      dispatch(fetchAuditTimeline(orderId))
    }
  }, [dispatch, orderId])

  useEffect(() => {
    loadDetail()
    return () => {
      dispatch(resetDetail())
    }
  }, [loadDetail, dispatch])

  // The slice now returns structured error envelopes ({ errorCode, message, status })
  // for all rejected thunks. v1 only needs the .message field for its snackbar.
  const errorMessage = (e) =>
    typeof e === 'string' ? e : (e && e.message) || 'Something went wrong'

  useEffect(() => {
    if (error) {
      dispatch(
        setSnackbar({
          message: errorMessage(error),
          severity: 'error',
          action: true,
          autoHideDuration: 4000,
        }),
      )
    }
  }, [error, dispatch])

  useEffect(() => {
    if (submitError) {
      dispatch(
        setSnackbar({
          message: errorMessage(submitError),
          severity: 'error',
          action: true,
          autoHideDuration: 4000,
        }),
      )
    }
  }, [submitError, dispatch])

  const handleOpenRelease = (hold) => {
    setTargetHold(hold)
    setReleaseModalOpen(true)
  }

  const handleConfirmRelease = async (comment) => {
    setReleaseModalOpen(false)
    const result = await dispatch(
      doReleaseHold({
        orderId,
        holdId: targetHold.holdId,
        role: mockRole,
        username: 'demo-user',
        comment,
      }),
    )
    if (!result.error) {
      dispatch(
        setSnackbar({
          message: `Hold ${targetHold.holdCode} released successfully`,
          severity: 'success',
          action: true,
          autoHideDuration: 3000,
        }),
      )
      loadDetail()
    }
  }

  const handleConfirmReleaseAll = async (comment) => {
    setReleaseAllModalOpen(false)
    const result = await dispatch(
      doReleaseAll({ orderId, role: mockRole, username: 'demo-user', comment }),
    )
    if (!result.error) {
      dispatch(
        setSnackbar({
          message: 'All releasable holds released',
          severity: 'success',
          action: true,
          autoHideDuration: 3000,
        }),
      )
      loadDetail()
    }
  }

  const handlePrefill = async () => {
    const result = await dispatch(doPrefillAllocation(orderId))
    if (!result.error) {
      dispatch(
        setSnackbar({
          message: 'Allocation prefilled with suggested quantities',
          severity: 'success',
          action: true,
          autoHideDuration: 3000,
        }),
      )
    }
  }

  const handleSubmitAllocation = async () => {
    const lines = allocationLines.map((l) => ({
      lineNumber: l.lineNumber,
      allocateQty: l.allocateQty ?? l.suggestedAllocateQty,
    }))
    const result = await dispatch(
      doSubmitAllocation({ orderId, role: mockRole, username: 'demo-user', lines }),
    )
    if (!result.error) {
      dispatch(
        setSnackbar({
          message: 'Allocation submitted successfully',
          severity: 'success',
          action: true,
          autoHideDuration: 3000,
        }),
      )
      loadDetail()
    }
  }

  const handleDefer = () => {
    dispatch(
      setSnackbar({
        message: 'Allocation deferred',
        severity: 'info',
        action: true,
        autoHideDuration: 3000,
      }),
    )
  }

  const releasableHolds = holds.filter((h) => h.releasable && !h.released)
  const activeHolds = holds.filter((h) => !h.released)

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#f2f5f2', minHeight: '100vh' }}>
      <HoldAllocationNav mockRole={mockRole} onRoleChange={setMockRole} />
      <Backdrop open={isFetching} sx={{ zIndex: 9999 }}>
        <CircularProgress color='inherit' />
      </Backdrop>

      <Container maxWidth='xl' sx={{ pt: 3, pb: 8 }}>
        {/* Back + title */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid
            size={{ xs: 12 }}
            sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/hold-allocation')}
              sx={{
                fontFamily: 'AtlasGroteskRegular',
                textTransform: 'none',
                fontSize: '0.85rem',
                color: '#145a4b',
                minWidth: 0,
                p: '4px 8px',
              }}
            >
              Orders
            </Button>
            <Divider orientation='vertical' flexItem sx={{ height: 24 }} />
            <PageTitle title={`Order ${orderId}`} />
            {orderDetail && (
              <Box
                sx={{
                  ml: 1,
                  px: 1.5,
                  py: 0.5,
                  backgroundColor: orderDetail.orderStatus === 'ON_HOLD' ? '#fdecea' : '#e8f5e9',
                  color: orderDetail.orderStatus === 'ON_HOLD' ? '#cc3f0c' : '#2a7b5a',
                  borderRadius: 1,
                  fontSize: '0.8rem',
                  fontFamily: 'AtlasGroteskRegular',
                  fontWeight: 600,
                }}
              >
                {orderDetail.orderStatus?.replace(/_/g, ' ')}
              </Box>
            )}
          </Grid>
        </Grid>

        {/* Order info cards */}
        {orderDetail && (
          <Box sx={{ mb: 4 }}>
            <OrderInfoCard orderDetail={orderDetail} />
          </Box>
        )}

        {/* Hold Details */}
        {holds.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1.5,
              }}
            >
              <SubHeader title='Hold Details' />
              {releasableHolds.length > 1 && (
                <Button
                  variant='contained'
                  color='secondary'
                  size='small'
                  startIcon={<LockOpenIcon />}
                  onClick={() => setReleaseAllModalOpen(true)}
                  disabled={isSubmitting}
                  sx={{
                    fontFamily: 'AtlasGroteskRegular',
                    textTransform: 'none',
                    fontSize: '0.82rem',
                  }}
                >
                  Release All ({releasableHolds.length})
                </Button>
              )}
            </Box>
            <HoldDetailsTable
              holds={holds}
              onRelease={handleOpenRelease}
              isSubmitting={isSubmitting}
            />
          </Box>
        )}

        {/* Allocation Lines */}
        {allocationLines.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Box sx={{ mb: 1.5 }}>
              <SubHeader title='Allocation' />
            </Box>
            <AllocationTable
              allocationLines={allocationLines}
              onPrefill={handlePrefill}
              onSubmit={handleSubmitAllocation}
              onDefer={handleDefer}
              isSubmitting={isSubmitting}
              orderId={orderId}
            />
          </Box>
        )}

        {/* Audit Timeline */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ mb: 1.5 }}>
            <SubHeader title='Audit Timeline' />
          </Box>
          <Box
            sx={{
              backgroundColor: '#ffffff',
              border: '1px solid #e7e7e7',
              borderRadius: 2,
              p: 3,
            }}
          >
            <AuditTimeline events={auditEvents} />
          </Box>
        </Box>
      </Container>

      {/* Release single hold modal */}
      <ReleaseHoldModal
        open={releaseModalOpen}
        hold={targetHold}
        isReleaseAll={false}
        onConfirm={handleConfirmRelease}
        onClose={() => setReleaseModalOpen(false)}
        isSubmitting={isSubmitting}
      />

      {/* Release all modal */}
      <ReleaseHoldModal
        open={releaseAllModalOpen}
        hold={null}
        isReleaseAll
        onConfirm={handleConfirmReleaseAll}
        onClose={() => setReleaseAllModalOpen(false)}
        isSubmitting={isSubmitting}
      />

      <GenericSnackbar />
    </Box>
  )
}

export default HoldAllocationDetail
